// src/pages/AccessUsersPage.tsx — Slice F (admin user maintenance) +
// Phase 9 (Users / Pending / Signed In / Audit Log tabs + Maintenance panel).
//
// SSO-admin-only — gated client-side; server also enforces requireSsoAdmin.
//
// Tab structure:
//   - Users    : active users, filtered by role + search
//   - Pending  : TWO kinds of pending — (a) approved_users rows whose owner
//                hasn't completed their first SSO sign-in (no hub_users row
//                yet, fetched via /api/access/users/pending) and (b) hub_users
//                rows with sign_on_status='pending' (PIN invitations waiting
//                for redemption).
//   - Signed In: read-only view ordered by last_login (grouped or flat)
//   - Audit    : paginated audit.events with filters
//
// Maintenance toggle lives in a "Danger zone" expander above the tabs so it
// is one click away but doesn't dominate the screen.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Field,
  PermissionLoading,
  SearchInput,
  Stamp,
  TopBar,
} from "../components/ui";
import type { AccessTabId, AccessUser, UsersListResponse } from "./access/types";
import { useDepartments } from "../hooks/useDepartments";
import type { DeptRow } from "../hooks/useDepartments";
import { InviteUserForm } from "./access/InviteUserForm";
import { PendingTab } from "./access/PendingTab";
import { SelfRegisteredSection } from "./access/SelfRegisteredSection";
import { SignedInTab } from "./access/SignedInTab";
import { AuditLogTab } from "./access/AuditLogTab";
import { MaintenancePanel } from "./access/MaintenancePanel";
import { UserGroupsTab } from "./access/UserGroupsTab";

// ---------------------------------------------------------------------------
// SSO-pending — approved_users rows that haven't yet completed first sign-in.
// Distinct from PendingTab (which renders hub_users sign_on_status='pending'
// PIN invitations). Shape mirrors /api/access/users/pending output.
// ---------------------------------------------------------------------------
interface SsoPendingUser {
  email: string;
  first_name: string;
  last_name: string;
  departments: string[];
  is_admin: boolean;
  added_by: string;
  added_at: string;
}

interface SsoPendingResponse {
  pending: SsoPendingUser[];
}

// ---------------------------------------------------------------------------
// Phase 10 / Feature 5b + 5c — Departments list / create.
// The department list (GET /api/access/departments) is now loaded via the
// shared useDepartments() hook so this filter row and the Edit User picker
// can't drift apart (issue #15). DeptRow is re-used from the hook. POST
// /api/access/departments is still issued by NewDepartmentModal below.
// ---------------------------------------------------------------------------

type FilterId =
  | "all"
  | "admins"
  | "hr"
  | "news"
  | "alerts"
  | "managers"
  | "rota"
  // Sign-In System: users with duty sign-in enabled (hub_users.sign_in_enabled).
  | "signin"
  // Phase 10 / Feature 5a: filter by sign-in method.
  | "pin"
  | "sso"
  | "blocked";

const FILTERS: ReadonlyArray<{ id: FilterId; label: string }> = [
  { id: "all",      label: "All" },
  { id: "admins",   label: "Admins" },
  { id: "hr",       label: "HR" },
  { id: "news",     label: "News" },
  { id: "alerts",   label: "Alerts" },
  { id: "managers", label: "Managers" },
  { id: "rota",     label: "DM/MOM/EM/DC/DF" },
  { id: "signin",   label: "Sign-In Enabled" },
  // Phase 10 / Feature 5a — PIN-only vs SSO. has_pin / has_sso are
  // already on the access.ts response.
  { id: "pin",      label: "PIN" },
  { id: "sso",      label: "SSO" },
  { id: "blocked",  label: "Blocked" },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AccessUsersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, loading: meLoading, refresh: refreshMe } = useCurrentUser();

  const [tab, setTab] = useState<AccessTabId>("users");
  const [users, setUsers] = useState<AccessUser[] | null>(null);
  const [ssoPending, setSsoPending] = useState<SsoPendingUser[] | null>(null);
  const [ssoPendingBusy, setSsoPendingBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterId>("all");
  const [inviting, setInviting] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  // Phase 5d -- CEO Newsletter settings expander (SSO-admin only).
  const [showCeoConfig, setShowCeoConfig] = useState(false);

  // Phase 10 / Feature 5b + 5c — admin-only department filter pills and
  // the "+ New Department" modal. The pill row sits below the role
  // filter row; selecting departments AND-combines with the role filter
  // and OR-combines within the selected set.
  //
  // Issue #15 — the list itself now comes from the shared useDepartments()
  // hook. `loadDepartments` (the hook's reload) refreshes the pill row
  // after the New Department modal POSTs.
  const { departments, reload: loadDepartments } = useDepartments();
  const [deptFilter, setDeptFilter] = useState<Set<string>>(new Set());
  const [newDeptOpen, setNewDeptOpen] = useState(false);

  const [flash, setFlash] = useState<string | null>(
    typeof location.state === "object" && location.state !== null && "flash" in location.state
      ? String((location.state as { flash?: unknown }).flash ?? "")
      : null,
  );

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => {
      setFlash(null);
      window.history.replaceState({}, "", location.pathname);
    }, 3000);
    return () => window.clearTimeout(t);
  }, [flash, location.pathname]);

  const loadUsers = useCallback(async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        apiFetch<UsersListResponse>("/api/access/users"),
        apiFetch<SsoPendingResponse>("/api/access/users/pending"),
      ]);
      setUsers(usersRes.users);
      setSsoPending(pendingRes.pending);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load users.");
    }
  }, []);

  /** Revoke an SSO-pending approval (approved_users row, no hub_users yet). */
  const revokeSsoPending = useCallback(async (email: string) => {
    if (!window.confirm("Revoke approval for " + email + "? They will not be able to sign in.")) {
      return;
    }
    setSsoPendingBusy(email);
    try {
      await apiFetch("/api/access/users/pending/" + encodeURIComponent(email), { method: "DELETE" });
      setFlash("Approval revoked for " + email + ".");
      // Refresh both lists; the row should disappear from ssoPending.
      const pendingRes = await apiFetch<SsoPendingResponse>("/api/access/users/pending");
      setSsoPending(pendingRes.pending);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Revoke failed.");
    } finally {
      setSsoPendingBusy(null);
    }
  }, []);

  /** Phase 11.2 — promote an SSO-pending approval to active without
   *  waiting for the user to complete first Microsoft sign-in. POST
   *  /api/access/users/pending/:email/approve provisions the hub_users
   *  row; entra_object_id still backfills on first sign-in via the
   *  email match in buildSsoSession (no SPA change needed for that). */
  const approveSsoPending = useCallback(async (email: string) => {
    setSsoPendingBusy(email);
    try {
      await apiFetch(
        "/api/access/users/pending/" + encodeURIComponent(email) + "/approve",
        { method: "POST" },
      );
      setFlash("Approved " + email + ". They're now an active user.");
      // Refresh both lists — the row moves from ssoPending into the
      // active Users tab.
      await loadUsers();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Approve failed.");
    } finally {
      setSsoPendingBusy(null);
    }
  }, [loadUsers]);

  useEffect(() => {
    if (!me?.isAdmin || !me.isSsoAdmin) return;
    loadUsers();
  }, [me?.isAdmin, me?.isSsoAdmin, loadUsers]);

  const toggleDept = useCallback((name: string) => {
    setDeptFilter((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const clearDeptFilter = useCallback(() => {
    setDeptFilter(new Set());
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (u.sign_on_status === "pending") return false;
      if (q) {
        const haystack = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // Phase 10 / Feature 5b — department filter ANDs with the role
      // filter and ORs within the selected set. An empty set means
      // "no department restriction".
      if (deptFilter.size > 0) {
        const hit = u.departments.some((d) => deptFilter.has(d));
        if (!hit) return false;
      }
      switch (filter) {
        case "all":      return true;
        case "admins":   return u.is_admin;
        case "hr":       return u.is_hr_admin;
        case "news":     return u.is_news_admin;
        case "alerts":   return u.is_alerts_admin;
        case "managers": return u.is_manager;
        case "rota":     return u.is_dm || u.is_mom || u.is_em || u.is_dc || u.is_df;
        case "signin":   return u.sign_in_enabled;
        // Phase 10 / Feature 5a — PIN-only excludes anyone who also has
        // an Entra identity; SSO matches anyone with an Entra identity
        // (regardless of whether they also have a PIN).
        case "pin":      return u.has_pin && !u.has_sso;
        case "sso":      return u.has_sso;
        case "blocked":  return u.is_blocked;
      }
    });
  }, [users, query, filter, deptFilter]);

  const pendingUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => u.sign_on_status === "pending");
  }, [users]);

  if (meLoading) {
    return <PermissionLoading title="Users" onBack={() => navigate("/apps")} />;
  }
  if (me && me.isAdmin && !me.isSsoAdmin) {
    return (
      <div>
        <TopBar title="Users" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 17,
                fontWeight: 700,
                color: C.ink,
                marginBottom: 6,
              }}
            >
              This page is for SSO admins only.
            </div>
            <div
              style={{
                fontFamily: F.body,
                fontSize: 13,
                color: C.inkSoft,
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              User maintenance is gated on a Microsoft sign-in. Sign out and use your work email
              if you need to manage users.
            </div>
            <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={() => navigate("/apps")}>
              Back to apps
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Pending tab combines two kinds: PIN invitations (hub_users.sign_on_status='pending')
  // and SSO approvals waiting for first sign-in (approved_users with no hub_users row).
  const totalPending = pendingUsers.length + (ssoPending?.length ?? 0);
  const tabSub: Record<AccessTabId, string> = {
    users: "Active users",
    pending: `${totalPending} awaiting first sign-in`,
    signedIn: "Recent activity",
    audit: "Who did what, when",
    groups: "Newsletter distribution",
  };

  return (
    <div>
      <TopBar
        title="User Access"
        sub={tabSub[tab]}
        onBack={() => navigate("/apps")}
      />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {flash && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: C.sageBg,
              border: `1px solid ${C.sageLt}`,
              borderRadius: 12,
              padding: "10px 14px",
              fontFamily: F.body,
              fontSize: 14,
              fontWeight: 700,
              color: C.sageDk,
            }}
          >
            {flash}
          </div>
        )}

        {/* Maintenance expander */}
        <button
          type="button"
          onClick={() => setShowMaintenance((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 12,
            background: me?.systemMaintenance?.active ? C.signalBg : C.paperWarm,
            border: `1px solid ${me?.systemMaintenance?.active ? "rgba(184,58,43,0.22)" : C.hairlineLt}`,
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: me?.systemMaintenance?.active ? C.signalDk : C.inkSoft,
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
          }}
        >
          <span style={{ flex: 1 }}>
            {me?.systemMaintenance?.active
              ? "⚠ Maintenance mode is ON — non-admin users are blocked."
              : "System maintenance"}
          </span>
          <span style={{ fontFamily: F.mono, fontSize: 11 }}>
            {showMaintenance ? "▾ Hide" : "▸ Show"}
          </span>
        </button>

        {showMaintenance && me?.systemMaintenance && (
          <MaintenancePanel
            state={me.systemMaintenance}
            onChanged={() => {
              refreshMe();
            }}
          />
        )}

        {/* Phase 5d -- CEO Newsletter settings expander (SSO-admin only;
            we're already inside the isSsoAdmin gate above). Mirrors the
            Maintenance expander pattern. */}
        <button
          type="button"
          onClick={() => setShowCeoConfig((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 12,
            background: C.heatherBg,
            border: `1px solid ${C.heatherLt}`,
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: C.heatherDk,
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
          }}
        >
          <span style={{ flex: 1 }}>CEO Newsletter settings</span>
          <span style={{ fontFamily: F.mono, fontSize: 11 }}>
            {showCeoConfig ? "▾ Hide" : "▸ Show"}
          </span>
        </button>

        {showCeoConfig && <CeoConfigPanel onSaved={() => setFlash("CEO Newsletter settings saved.")} />}

        {/* Sign-In System (Build 2) -- second door to Group Manager (also a
            tile on the Apps grid). Chat-group CRUD + membership live there. */}
        <button
          type="button"
          onClick={() => navigate("/group-manager")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            background: C.heatherBg,
            border: `1px solid ${C.heatherLt}`,
            borderRadius: 14,
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: C.heatherDk,
              color: C.brassGlow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: F.serif,
              fontWeight: 700,
              fontSize: 16,
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            G
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.heatherDk, letterSpacing: -0.2 }}>
              Group Manager
            </div>
            <div style={{ fontSize: 12.5, color: C.inkSoft, fontWeight: 600, marginTop: 2, lineHeight: 1.35 }}>
              Create chat groups and manage membership.
            </div>
          </div>
          <span style={{ color: C.heatherDk, fontSize: 20, fontWeight: 700, flexShrink: 0 }}>›</span>
        </button>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
          <TabPill on={tab === "users"} onClick={() => setTab("users")}>
            Users{users ? ` (${users.filter((u) => u.sign_on_status !== "pending").length})` : ""}
          </TabPill>
          <TabPill on={tab === "pending"} onClick={() => setTab("pending")} badge={totalPending}>
            Pending
          </TabPill>
          <TabPill on={tab === "signedIn"} onClick={() => setTab("signedIn")}>
            Signed In
          </TabPill>
          <TabPill on={tab === "audit"} onClick={() => setTab("audit")}>
            Audit Log
          </TabPill>
          <TabPill on={tab === "groups"} onClick={() => setTab("groups")}>
            Groups
          </TabPill>
        </div>

        {/* Tab body */}
        {error && (
          <Card>
            <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div>
          </Card>
        )}

        {users === null && !error && tab !== "audit" && tab !== "groups" && <SkeletonStack />}

        {tab === "users" && users !== null && (
          <>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <SearchInput
                  value={query}
                  onChange={setQuery}
                  placeholder="Search by name or email"
                />
              </div>
              <Button variant="primary" onClick={() => navigate("/access/users/new")}>
                + Add staff (SSO)
              </Button>
              <Button variant="soft" onClick={() => setInviting(true)}>
                ✉ Invite volunteer (PIN)
              </Button>
            </div>

            <FilterPills active={filter} onChange={setFilter} />

            {/* Phase 10 / Feature 5b + 5c — department pill row. Admin-only
                (we're already inside an isSsoAdmin gate further up). */}
            <DepartmentPills
              departments={departments}
              selected={deptFilter}
              onToggle={toggleDept}
              onClear={clearDeptFilter}
              onAdd={() => setNewDeptOpen(true)}
            />

            {filtered.length === 0 ? (
              <Card>
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 15,
                    color: C.inkMute,
                    textAlign: "center",
                    padding: "12px 0",
                  }}
                >
                  {query
                    ? `No users match "${query}".`
                    : "No users in this filter yet."}
                </div>
              </Card>
            ) : (
              <Card padded={false}>
                {filtered.map((u, i) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isFirst={i === 0}
                    onTap={() => navigate(`/access/users/${u.id}`)}
                  />
                ))}
              </Card>
            )}
          </>
        )}

        {tab === "pending" && users !== null && (
          <>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ flex: 1 }} />
              <Button variant="primary" onClick={() => setInviting(true)}>
                ✉ Invite volunteer (PIN)
              </Button>
            </div>
            {/* Phase 10 / Feature 4 — self-registered (hub_pending_registrations).
                Rendered first per the kickoff so admins see the freshest signal
                above the older "approved-awaiting-first-signin" and PIN-invite
                lists.

                Phase 11 / Q5 — after approve, refetch users and navigate
                to the newly-provisioned user's edit page so Chris can
                immediately set apps + role flags + line manager. */}
            <SelfRegisteredSection
              departments={departments ?? []}
              onChanged={(msg) => {
                setFlash(msg);
                loadUsers();
              }}
              onApproved={async (email) => {
                try {
                  const res = await apiFetch<UsersListResponse>("/api/access/users");
                  setUsers(res.users);
                  const newUser = res.users.find(
                    (u) => u.email.toLowerCase() === email.toLowerCase(),
                  );
                  if (newUser) {
                    navigate(`/access/users/${newUser.id}`);
                  }
                } catch {
                  // Refresh failed — stay on the pending tab; the
                  // onChanged flash already confirms the approve so
                  // the admin isn't left wondering.
                  loadUsers();
                }
              }}
            />
            {ssoPending && ssoPending.length > 0 && (
              <Card padded={false}>
                <div style={{ padding: "12px 16px 8px" }}>
                  <Eyebrow>Approved — awaiting first SSO sign-in</Eyebrow>
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: C.inkMute,
                      marginTop: 4,
                      lineHeight: 1.45,
                    }}
                  >
                    Approved staff who haven't yet completed their first Microsoft sign-in.
                    They'll move to the main Users list automatically on first login.
                  </div>
                </div>
                {ssoPending.map((u, i) => (
                  <SsoPendingRow
                    key={u.email}
                    user={u}
                    isFirst={i === 0}
                    busy={ssoPendingBusy === u.email}
                    onApprove={() => approveSsoPending(u.email)}
                    onRevoke={() => revokeSsoPending(u.email)}
                  />
                ))}
              </Card>
            )}
            <PendingTab
              pending={pendingUsers}
              onRefresh={loadUsers}
              onOpen={(id) => navigate(`/access/users/${id}`)}
            />
          </>
        )}

        {tab === "signedIn" && users !== null && (
          <SignedInTab
            users={users.filter((u) => u.sign_on_status !== "pending")}
            onOpen={(id) => navigate(`/access/users/${id}`)}
          />
        )}

        {tab === "audit" && <AuditLogTab />}

        {tab === "groups" && <UserGroupsTab />}
      </div>

      {inviting && (
        <InviteUserForm
          onClose={() => setInviting(false)}
          onSent={(result) => {
            setInviting(false);
            setFlash(
              result.emailed
                ? `Invitation sent to ${result.email}.`
                : `Invite created for ${result.email}; email failed. Use Reveal PIN from their record.`,
            );
            loadUsers();
            setTab("pending");
          }}
          onJumpToExisting={(id) => {
            setInviting(false);
            navigate(`/access/users/${id}`);
          }}
        />
      )}

      {newDeptOpen && (
        <NewDepartmentModal
          existingNames={departments?.map((d) => d.name.toLowerCase()) ?? []}
          onClose={() => setNewDeptOpen(false)}
          onCreated={(d) => {
            setNewDeptOpen(false);
            setFlash(`Department "${d.name}" added.`);
            loadDepartments();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabPill
// ---------------------------------------------------------------------------

function TabPill({
  on,
  onClick,
  badge,
  children,
}: {
  on: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 14px",
        borderRadius: 999,
        border: on ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
        background: on ? C.brassDk : C.card,
        color: on ? C.brassGlow : C.inkSoft,
        fontFamily: F.body,
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: 0.3,
        cursor: "pointer",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{children}</span>
      {typeof badge === "number" && badge > 0 && (
        <span
          style={{
            padding: "1px 7px",
            borderRadius: 999,
            background: on ? C.brassGlow : C.signal,
            color: on ? C.brassDk : "#FFFCEC",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// CeoConfigPanel (Phase 5d) — GET/PUT /api/access/ceo-config. SSO-admin only.
// Sets the CEO name / title / header label used across the CEO Newsletter
// (masthead, email subject, watermark, PDF footer).
// ---------------------------------------------------------------------------

interface CeoConfig {
  ceoName: string;
  ceoTitle: string;
  headerLabel: string;
  updatedAt?: string;
}

function CeoConfigPanel({ onSaved }: { onSaved: () => void }) {
  const [ceoName, setCeoName] = useState("");
  const [ceoTitle, setCeoTitle] = useState("");
  const [headerLabel, setHeaderLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<CeoConfig>("/api/access/ceo-config")
      .then((cfg) => {
        if (cancelled) return;
        setCeoName(cfg.ceoName ?? "");
        setCeoTitle(cfg.ceoTitle ?? "");
        setHeaderLabel(cfg.headerLabel ?? "");
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : "Couldn't load CEO settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const canSave =
    !saving && ceoName.trim().length > 0 && ceoTitle.trim().length > 0 && headerLabel.trim().length > 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setErr(null);
    try {
      await apiFetch("/api/access/ceo-config", {
        method: "PUT",
        body: JSON.stringify({
          ceoName: ceoName.trim(),
          ceoTitle: ceoTitle.trim(),
          headerLabel: headerLabel.trim(),
        }),
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't save CEO settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Eyebrow c={C.heatherDk}>CEO Newsletter settings</Eyebrow>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12, color: C.inkMute, marginTop: 4, marginBottom: 10, lineHeight: 1.45 }}>
        Used across the CEO Newsletter masthead, publish email, PDF footer, and watermark.
      </div>
      {loading ? (
        <div aria-hidden="true" style={{ height: 180, background: C.paperWarm, borderRadius: 12, border: `1px solid ${C.hairlineLt}` }} />
      ) : (
        <>
          <Field label="CEO name" value={ceoName} onChange={setCeoName} max={120} ph="e.g. Chris Price" />
          <Field label="CEO title" value={ceoTitle} onChange={setCeoTitle} max={120} ph="e.g. Chief Executive" />
          <Field label="Header label" value={headerLabel} onChange={setHeaderLabel} max={120} ph="e.g. CEO Update" />
          {err && (
            <div style={{ padding: 10, borderRadius: 10, background: C.signalBg, border: `1px solid rgba(184,58,43,0.22)`, color: C.signalDk, fontFamily: F.body, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
              {err}
            </div>
          )}
          <Button variant="primary" onClick={save} disabled={!canSave}>
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// FilterPills
// ---------------------------------------------------------------------------

function FilterPills({
  active,
  onChange,
}: {
  active: FilterId;
  onChange: (f: FilterId) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {FILTERS.map((f) => {
        const isOn = f.id === active;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(f.id)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              border: isOn ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
              background: isOn ? C.brassBg : C.card,
              color: isOn ? C.brassDk : C.inkSoft,
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.2,
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SsoPendingRow — approved_users row, no hub_users yet. Different actions from
// the invitation-pending PendingTab rows (no PIN to resend; only revoke).
// ---------------------------------------------------------------------------

function SsoPendingRow({
  user,
  isFirst,
  busy,
  onApprove,
  onRevoke,
}: {
  user: SsoPendingUser;
  isFirst: boolean;
  busy: boolean;
  onApprove: () => void;
  onRevoke: () => void;
}) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 12 }}>
        <Avatar first={user.first_name} last={user.last_name} size={44} tone="brass" />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: F.serif,
                fontSize: 16,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: -0.2,
              }}
            >
              {user.first_name || "(no name)"} {user.last_name}
            </span>
            <Stamp tone="heather">Approved · SSO</Stamp>
            {user.is_admin && <Stamp tone="brass">Admin</Stamp>}
          </div>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.inkSoft,
              fontWeight: 600,
              marginTop: 2,
            }}
          >
            {user.email}
          </div>
          {user.departments.length > 0 && (
            <div
              style={{
                fontFamily: F.body,
                fontSize: 11,
                color: C.inkMute,
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              {user.departments.join(", ")}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {/* Phase 11.2 — Approve primary, Revoke secondary. Matches the
            SelfRegisteredRow button order below so the Pending tab
            reads consistently top-to-bottom. */}
        <Button variant="primary" onClick={onApprove} disabled={busy}>
          {busy ? "Working…" : "Approve"}
        </Button>
        <Button variant="ghost" onClick={onRevoke} disabled={busy}>
          Revoke approval
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserRow
// ---------------------------------------------------------------------------

function UserRow({
  user,
  isFirst,
  onTap,
}: {
  user: AccessUser;
  isFirst: boolean;
  onTap: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr auto",
        gap: 12,
        padding: "12px 16px",
        width: "100%",
        alignItems: "center",
        background: "transparent",
        border: "none",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
        cursor: "pointer",
        textAlign: "left",
        opacity: user.is_blocked ? 0.55 : 1,
      }}
    >
      <Avatar first={user.first_name} last={user.last_name} size={44} tone="brass" />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: F.serif,
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
            }}
          >
            {user.first_name} {user.last_name}
          </span>
          {user.is_blocked && <Stamp tone="signal">Blocked</Stamp>}
          {user.is_admin && <Stamp tone="brass">Admin</Stamp>}
          {user.is_hr_admin && <Stamp tone="signal">HR</Stamp>}
          {user.is_news_admin && <Stamp tone="sage">News</Stamp>}
          {user.is_alerts_admin && <Stamp tone="heather">Alerts</Stamp>}
          {user.is_manager && <Stamp tone="sage">Manager</Stamp>}
          {user.is_first_aider && <Stamp tone="signal">First aider</Stamp>}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 12,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.email}
        </div>
        {user.departments.length > 0 && (
          <div
            style={{
              fontFamily: F.body,
              fontSize: 11,
              color: C.inkMute,
              fontWeight: 500,
              marginTop: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.departments.join(", ")}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: F.mono,
          fontSize: 12,
          color: C.inkMute,
          fontWeight: 700,
          letterSpacing: 0.4,
          textAlign: "right",
        }}
      >
        {user.member_id_number !== null ? `#${user.member_id_number}` : "—"}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function SkeletonStack() {
  return (
    <>
      <SkeletonRow h={68} />
      <SkeletonRow h={68} />
      <SkeletonRow h={68} />
    </>
  );
}

function SkeletonRow({ h }: { h: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: h,
        background: C.paperWarm,
        borderRadius: 14,
        border: `1px solid ${C.hairlineLt}`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Phase 10 / Feature 5b + 5c — Department pills row + "+ New Department"
// ---------------------------------------------------------------------------

function DepartmentPills({
  departments,
  selected,
  onToggle,
  onClear,
  onAdd,
}: {
  departments: DeptRow[] | null;
  selected: Set<string>;
  onToggle: (name: string) => void;
  onClear: () => void;
  onAdd: () => void;
}) {
  if (departments === null) {
    return (
      <div
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 12,
          color: C.inkMute,
          padding: "4px 2px",
        }}
      >
        Loading departments…
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        alignItems: "center",
        paddingTop: 2,
      }}
    >
      <span
        style={{
          fontFamily: F.body,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: C.inkMute,
          marginRight: 4,
        }}
      >
        Dept
      </span>
      {departments.map((d) => {
        const on = selected.has(d.name);
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onToggle(d.name)}
            style={{
              padding: "5px 11px",
              borderRadius: 999,
              border: on ? `1.5px solid ${C.heatherDk}` : `1px solid ${C.hairline}`,
              background: on ? C.heatherBg : C.card,
              color: on ? C.heatherDk : C.inkSoft,
              fontFamily: F.body,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {d.name}
          </button>
        );
      })}
      {selected.size > 0 && (
        <button
          type="button"
          onClick={onClear}
          style={{
            padding: "5px 9px",
            background: "transparent",
            border: "none",
            color: C.inkMute,
            fontFamily: F.body,
            fontSize: 12,
            fontWeight: 700,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      )}
      <button
        type="button"
        onClick={onAdd}
        style={{
          padding: "5px 11px",
          borderRadius: 999,
          border: `1px dashed ${C.hairline}`,
          background: C.paperSoft,
          color: C.brassDk,
          fontFamily: F.body,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          marginLeft: "auto",
        }}
      >
        + New Department
      </button>
    </div>
  );
}

interface NewDeptResponse {
  ok: true;
  id: number;
  name: string;
  sort_order: number;
}

function NewDepartmentModal({
  existingNames,
  onClose,
  onCreated,
}: {
  existingNames: string[];
  onClose: () => void;
  onCreated: (d: { id: number; name: string }) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const trimmed = name.trim();
  const isDupe = existingNames.includes(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0 && trimmed.length <= 100 && !isDupe;

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiFetch<NewDeptResponse>("/api/access/departments", {
        method: "POST",
        body: JSON.stringify({ name: trimmed }),
      });
      onCreated({ id: res.id, name: res.name });
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.message || `Could not add department (${e.status}).`);
      } else {
        setErr((e as Error)?.message ?? "Could not add department.");
      }
    } finally {
      setBusy(false);
    }
  }, [canSubmit, trimmed, onCreated]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-dept-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43, 32, 20, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          borderRadius: 16,
          border: `1px solid ${C.hairline}`,
          boxShadow: C.shadowL,
          width: "100%",
          maxWidth: 420,
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          id="new-dept-title"
          style={{
            fontFamily: F.serif,
            fontSize: 18,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.2,
          }}
        >
          New department
        </div>
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 12,
            color: C.inkMute,
            lineHeight: 1.5,
          }}
        >
          Adds a pill to the filter row and a new option in the user-edit
          department multi-select. Sort order is auto-assigned (appends).
        </div>
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontFamily: F.body,
            fontSize: 12,
            fontWeight: 700,
            color: C.inkSoft,
          }}
        >
          Department name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 100))}
            placeholder="e.g. Heritage Skills"
            autoFocus
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                void submit();
              }
            }}
            style={{
              padding: "9px 12px",
              border: `1px solid ${isDupe ? C.signal : C.hairline}`,
              borderRadius: 10,
              fontFamily: F.body,
              fontSize: 14,
              color: C.ink,
              background: C.paperSoft,
              outline: "none",
            }}
          />
        </label>
        {isDupe && trimmed.length > 0 && (
          <div
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.signalDk,
              fontWeight: 700,
            }}
          >
            That department already exists.
          </div>
        )}
        {err && !isDupe && (
          <div
            role="alert"
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.signalDk,
              fontWeight: 700,
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              padding: "8px 10px",
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 4,
          }}
        >
          <Button variant="soft" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? "Adding…" : "Add department"}
          </Button>
        </div>
      </div>
    </div>
  );
}
